import { isHTTPError, isNetworkError, isTimeoutError } from 'ky'

/**
 * A refusal from the backend, carrying the status so a caller can tell
 * "sign in" from "you may not" from "this is not configured".
 */
export class ApiRequestError extends Error {
  readonly status: number
  readonly details: string[]

  constructor(status: number, message: string, details: string[] = []) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.details = details
  }
}

/**
 * What a refusal looks like on the wire.
 *
 * `message` is what the backend sends: blytz-utils owns the error body and puts
 * every reason the service gave into that one string. `error` and the two
 * arrays are the shape the service builds internally, kept here because a
 * direct answer from the service — a test double, or a handler that does not
 * pass through the wrapper — still uses it.
 */
type ProblemBody = {
  message?: string
  error?: string
  errors?: string[]
  missing?: string[]
}

/**
 * Name the failure that happened before any status arrived.
 *
 * Each of these carries status 0, because no status ever arrived. They are not
 * the same fault. Saying "could not be reached" for all of them sends the
 * reader to the network when the network is fine. A reply the parser rejects
 * means the request did arrive and something answered with something other
 * than JSON — a sign-in page, or the SPA fallback that serves index.html for
 * an unknown path. That reading costs real time during an incident.
 *
 * ky detects a network error with runtime-specific heuristics, so a runtime it
 * does not recognise can throw an error it does not wrap. The last line is for
 * that case: it states what is certain and claims nothing more.
 *
 * @param error - Whatever the request threw.
 * @returns One sentence that names the failure.
 */
function describeFailureBeforeStatus(error: unknown): string {
  if (isTimeoutError(error)) {
    return 'the service took too long to answer'
  }
  if (isNetworkError(error)) {
    return 'the service could not be reached'
  }
  if (error instanceof SyntaxError) {
    return 'the service sent a reply the app could not read'
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'the request was cancelled'
  }

  return 'the request failed before the service answered'
}

/**
 * Turn any thrown request into an ApiRequestError that says something useful.
 *
 * @param error - Whatever the request threw.
 * @returns The error to surface, never a rejection of its own.
 */
export async function toApiError(error: unknown): Promise<ApiRequestError> {
  if (!isHTTPError(error)) {
    return new ApiRequestError(0, describeFailureBeforeStatus(error))
  }

  let body: ProblemBody
  try {
    body = await error.response.json<ProblemBody>()
  } catch {
    body = {}
  }

  const details = body.errors ?? body.missing ?? []

  return new ApiRequestError(
    error.response.status,
    body.message ?? body.error ?? 'the service refused the request',
    details
  )
}
